import React from 'react';
import { toDateTimeString } from '../../../utils/date';
import {
  amountTdStyle,
  buttonStyle,
  exportGridStyle,
  filterGridStyle,
  footerStyle,
  inputStyle,
  memoInputStyle,
  mutedTextStyle,
  primaryButtonStyle,
  sectionLabelStyle,
  statusChipStyle,
  strongTextStyle,
  summaryCardStyle,
  summaryGridStyle,
  tableStyle,
  tableWrapStyle,
  tdStyle,
  thStyle,
} from './CardPaymentDepositModal.styles';

export function fmt(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString('ko-KR') : '0';
}

export function compactDate(value) {
  return String(value || '').replace(/-/g, '');
}

function statusText(status) {
  return status === 'confirmed' ? '입금확인' : '입금대기';
}

function paymentScopeText(row) {
  return row.revenue_event_id ? '변경결제' : '기본결제';
}

function paymentScopeBadgeStyle(hasRevenueEvent) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 6px',
    borderRadius: 999,
    border: '1px solid var(--border)',
    background: hasRevenueEvent ? 'rgba(37, 99, 235, 0.12)' : 'var(--bg)',
    color: hasRevenueEvent ? '#2563eb' : 'var(--text-muted)',
    fontSize: 11,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  };
}

export function CardDepositFilters({
  dateFrom,
  dateTo,
  status,
  loading,
  defaultRange,
  defaultMonthRange,
  onDateFrom,
  onDateTo,
  onStatus,
  onLoad,
}) {
  return (
    <div className="card-deposit-filter-grid" style={filterGridStyle}>
      <label style={sectionLabelStyle}>
        시작일
        <input type="date" value={dateFrom} onChange={(e) => onDateFrom(e.target.value)} style={inputStyle} />
      </label>
      <label style={sectionLabelStyle}>
        종료일
        <input type="date" value={dateTo} onChange={(e) => onDateTo(e.target.value)} style={inputStyle} />
      </label>
      <label style={sectionLabelStyle}>
        상태
        <select value={status} onChange={(e) => onStatus(e.target.value)} style={inputStyle}>
          <option value="">전체</option>
          <option value="pending">입금대기</option>
          <option value="confirmed">입금확인</option>
        </select>
      </label>
      <button type="button" onClick={onLoad} disabled={loading} style={primaryButtonStyle}>
        {loading ? '조회 중...' : '조회'}
      </button>
      <button
        type="button"
        onClick={() => {
          onDateFrom(defaultRange.dateFrom);
          onDateTo(defaultRange.dateTo);
          onStatus('');
        }}
        style={buttonStyle}
      >
        이번주 월~금
      </button>
      <button
        type="button"
        onClick={() => {
          onDateFrom(defaultMonthRange.dateFrom);
          onDateTo(defaultMonthRange.dateTo);
          onStatus('');
        }}
        style={buttonStyle}
      >
        이번달
      </button>
    </div>
  );
}

// 이체 파일 입금 계좌 — 창을 열면 이 회사가 저장한 기본값이 채워진다(없으면 빈 칸, 2026-10-29).
//   [기본값으로 저장] = 지금 칸 값을 이 회사 기본값으로 저장. 칸을 고치면 '저장됨' 표시가 풀린다.
export function CardDepositExportOptions({ exportConfig, setExportConfig, onSaveDefault, savingDefault, defaultSaved, onEdited }) {
  const setValue = (key, value) => { setExportConfig((prev) => ({ ...prev, [key]: value })); onEdited?.(); };
  return (
    // 마지막 칸 = 저장 버튼(휴대폰은 mobileCss 가 한 줄씩 쌓는다)
    <div className="card-deposit-export-grid" style={{ ...exportGridStyle, gridTemplateColumns: `${exportGridStyle.gridTemplateColumns} auto` }}>
      <label style={sectionLabelStyle}>
        은행코드
        <input value={exportConfig.bankCode} placeholder="예: 004" onChange={(e) => setValue('bankCode', e.target.value)} style={inputStyle} />
      </label>
      <label style={sectionLabelStyle}>
        계좌번호
        <input value={exportConfig.accountNumber} placeholder="입금받을 계좌번호" onChange={(e) => setValue('accountNumber', e.target.value)} style={inputStyle} />
      </label>
      <label style={sectionLabelStyle}>
        예금주
        <input value={exportConfig.accountHolder} placeholder="예금주" onChange={(e) => setValue('accountHolder', e.target.value)} style={inputStyle} />
      </label>
      <label style={sectionLabelStyle}>
        적요
        <input value={exportConfig.memo} placeholder="선택" onChange={(e) => setValue('memo', e.target.value)} style={inputStyle} />
      </label>
      <button type="button" onClick={onSaveDefault} disabled={savingDefault} style={buttonStyle}>
        {savingDefault ? '저장 중...' : defaultSaved ? '기본값 저장됨' : '기본값으로 저장'}
      </button>
    </div>
  );
}

export function CardDepositSummary({ summary, rows }) {
  const cards = [
    ['전체', `${fmt(summary?.count)}건`, `${fmt(summary?.total_amount)}원`],
    ['입금대기', `${fmt(summary?.pending_count)}건`, `${fmt(summary?.pending_amount)}원`],
    ['입금확인', `${fmt(summary?.confirmed_count)}건`, `${fmt(summary?.confirmed_amount)}원`],
    ['작성 행', `${fmt(rows.length)}줄`, '헤더 없이 생성'],
  ];
  return (
    <div className="card-deposit-summary-grid" style={summaryGridStyle}>
      {cards.map(([label, main, sub]) => (
        <div key={label} style={summaryCardStyle}>
          <span style={mutedTextStyle}>{label}</span>
          <strong style={strongTextStyle}>{main}</strong>
          <span style={mutedTextStyle}>{sub}</span>
        </div>
      ))}
    </div>
  );
}

export function CardDepositTable({ rows, savingId, updateRowMemo, toggleConfirmed, saveMemo }) {
  return (
    <div style={tableWrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>결제일</th>
            <th style={thStyle}>구분</th>
            <th style={thStyle}>여행코드</th>
            <th style={thStyle}>예약자</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>카드금액</th>
            <th style={thStyle}>입금상태</th>
            <th style={thStyle}>확인정보</th>
            <th style={thStyle}>메모</th>
            <th style={thStyle}>처리</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
                카드 결제건이 없습니다.
              </td>
            </tr>
          ) : rows.map((row) => {
            const confirmed = row.card_deposit_status === 'confirmed';
            return (
              <tr key={row.id}>
                <td style={tdStyle}>{row.payment_date || '-'}</td>
                <td style={tdStyle}>
                  <span style={paymentScopeBadgeStyle(row.revenue_event_id)}>{paymentScopeText(row)}</span>
                </td>
                <td style={tdStyle}>{row.travel_code}</td>
                <td style={tdStyle}>{row.customer_name || '-'}</td>
                <td style={amountTdStyle}>{fmt(row.amount)}</td>
                <td style={tdStyle}>
                  <span style={statusChipStyle(confirmed)}>{statusText(row.card_deposit_status)}</span>
                </td>
                <td style={tdStyle}>
                  {confirmed
                    ? `${row.card_deposit_confirmed_by_name || '확인자'} · ${row.card_deposit_confirmed_at ? toDateTimeString(row.card_deposit_confirmed_at) : '-'}`
                    : '-'}
                </td>
                <td style={tdStyle}>
                  <input
                    value={row.card_deposit_memo || ''}
                    onChange={(e) => updateRowMemo(row.id, e.target.value)}
                    style={memoInputStyle}
                    placeholder="입금 확인 메모"
                  />
                </td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" disabled={savingId === row.id} onClick={() => toggleConfirmed(row)} style={buttonStyle}>
                      {confirmed ? '확인취소' : '입금확인'}
                    </button>
                    <button type="button" disabled={savingId === row.id} onClick={() => saveMemo(row)} style={buttonStyle}>
                      메모저장
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function CardDepositFooter({ onClose, downloadExcel, downloading, loading }) {
  return (
    <div className="card-deposit-footer" style={footerStyle}>
      <span style={mutedTextStyle}>
        결제일 기준 카드 결제만 조회합니다. 수익 이벤트의 변경 결제도 같은 결제 원장에 저장되어 이 목록에 함께 표시됩니다.
      </span>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={onClose} style={buttonStyle}>닫기</button>
        <button type="button" onClick={downloadExcel} disabled={downloading || loading} style={primaryButtonStyle}>
          {downloading ? '다운로드 중...' : '엑셀 다운로드'}
        </button>
      </div>
    </div>
  );
}
